import React, { Fragment, useContext, useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Row, Col, Card, CardBody, FormGroup, Label, Input } from 'reactstrap';
import { useTranslation } from 'react-i18next';

import { GlobalContext } from '../../store/Provider';
import { setAlert } from '../../store/actions/alerts';
import { fetchStatisticsData, fetchBenchmarkData } from '../../services/StatisticsService';
import { getDocuments } from '../../store/actions/documents';
import StatisticsChart from './StatisticsChart';
import PageSpinner from '../layout/PageSpinner';
import './StatisticsCharts.css';

/**
 * Parse a DD/MM/YYYY date string and return its ISO week number and year.
 */
function getWeekYear(dateStr) {
  const [day, month, year] = dateStr.split('/').map(Number);
  const d = new Date(Date.UTC(year, month - 1, day));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const week = Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
  return { week, year: d.getUTCFullYear() };
}

/**
 * The entity that issued a document: PRO (Propigs), WVL, LVF, CVM or COM.
 *
 * On the Drupal document this is field_deflvnr, which views.view.documents
 * exports under the alias firmacode_leverancier - the same way it exports
 * field_defvsnr as factuurvoorstelnummer. The value is already in the list the
 * dropdown is built from.
 */
function documentDeflvnr(doc) {
  if (!doc) {
    return undefined;
  }
  return doc.firmacode_leverancier || undefined;
}

function parseDocumentDate(dateStr) {
  const [day, month, year] = dateStr.split('/').map(Number);
  return new Date(year, month - 1, day);
}

function getPreviousWeekYear(dateStr) {
  const [day, month, year] = dateStr.split('/').map(Number);
  const d = new Date(Date.UTC(year, month - 1, day));
  d.setUTCDate(d.getUTCDate() - 7);

  const prevDay = String(d.getUTCDate()).padStart(2, '0');
  const prevMonth = String(d.getUTCMonth() + 1).padStart(2, '0');
  const prevYear = d.getUTCFullYear();

  return getWeekYear(`${prevDay}/${prevMonth}/${prevYear}`);
}

const StatisticsComponent = () => {
  const { documentsState, suppliersState, authState, alertsDispatch, documentsDispatch } =
    useContext(GlobalContext);
  const { t } = useTranslation();
  const location = useLocation();

  const [selectedFacvstnr, setSelectedFacvstnr] = useState('');
  const [statisticsData, setStatisticsData] = useState(null);
  const [afiBenchmarkValue, setAfiBenchmarkValue] = useState(null);
  const [vleesdikteBenchmarkValue, setVleesdikteBenchmarkValue] = useState(null);
  const [gewichtBenchmarkValue, setGewichtBenchmarkValue] = useState(null);
  const [spekdikteBenchmarkValue, setSpekdikteBenchmarkValue] = useState(null);
  const [kwaliteitstoeslagBenchmarkValue, setKwaliteitstoeslagBenchmarkValue] = useState(null);
  const [gewichtstoeslagBenchmarkValue, setGewichtstoeslagBenchmarkValue] = useState(null);
  const [loading, setLoading] = useState(false);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const documentsRequested = useRef(false);

  /**
   * Load the documents this page's dropdown is built from.
   *
   * Nothing else on the page fetches them: the header does, but only during its
   * own bootstrap and behind a permissions check that may not have resolved
   * yet. Landing here directly therefore used to show an empty dropdown until
   * the user visited the documents page, which remounted the header and ran the
   * fetch again.
   *
   * The request's own result drives the spinner rather than
   * documentsState.documents.fetching, which is never cleared when a fetch
   * fails and would leave the spinner up for good.
   */
  useEffect(() => {
    if (documentsRequested.current) {
      return;
    }
    // getDocuments indexes into permissions, so it cannot run before they land.
    if (!authState || !authState.permissions) {
      return;
    }
    if ((documentsState.documents.list || []).length > 0) {
      return;
    }

    documentsRequested.current = true;
    setDocumentsLoading(true);

    Promise.resolve(getDocuments(authState.permissions)(documentsDispatch))
      .then((res) => {
        if (res && res.code !== 200) {
          setAlert(
            res.msg || t('alerts.errors.documents.couldNotFetch'),
            'danger'
          )(alertsDispatch);
        }
      })
      .finally(() => setDocumentsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authState && authState.permissions, documentsState.documents.list]);

  // Filter documents list to only Afrekeningsborderel type
  const afrekeningDocuments = (documentsState.documents.list || []).filter(
    (doc) => doc.filetype === 'Afrekeningsborderel'
  );

  // Pre-fill dropdown from URL query param, otherwise select latest document once
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const facvstnr = params.get('facvstnr');

    if (facvstnr) {
      if (facvstnr !== selectedFacvstnr) {
        setSelectedFacvstnr(facvstnr);
      }
      return;
    }

    if (!selectedFacvstnr && afrekeningDocuments.length > 0) {
      const latestDoc = [...afrekeningDocuments].sort(
        (a, b) => parseDocumentDate(b.factuurvoorsteldatum) - parseDocumentDate(a.factuurvoorsteldatum)
      )[0];

      if (latestDoc) {
        setSelectedFacvstnr(latestDoc.factuurvoorstelnummer);
      }
    }
  }, [location.search, afrekeningDocuments, selectedFacvstnr]);

  // Fetch statistics + benchmark data whenever the selected document changes
  useEffect(() => {
    if (!selectedFacvstnr) {
      setStatisticsData(null);
      setAfiBenchmarkValue(null);
      setVleesdikteBenchmarkValue(null);
      setGewichtBenchmarkValue(null);
      setSpekdikteBenchmarkValue(null);
      setKwaliteitstoeslagBenchmarkValue(null);
      setGewichtstoeslagBenchmarkValue(null);
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      try {
        // Find the selected document to get its date for the benchmark call
        const selectedDoc = afrekeningDocuments.find(
          (doc) => doc.factuurvoorstelnummer === selectedFacvstnr
        );

        // Build the benchmark fetch promise if we have the needed info
        const benchmarkPromise = selectedDoc && suppliersState.suppliers.activeID
          ? (() => {
              const { week, year } = getPreviousWeekYear(selectedDoc.factuurvoorsteldatum);
              return fetchBenchmarkData(
                suppliersState.suppliers.activeID,
                week, year,
                week, year
              );
            })()
          : Promise.resolve(null);

        // Fetch statistics and benchmark in parallel
        const [data, benchmarkRaw] = await Promise.all([
          fetchStatisticsData(selectedFacvstnr, documentDeflvnr(selectedDoc)),
          benchmarkPromise,
        ]);

        setStatisticsData(data);

        // Extract benchmark values from the drgmbmk / DELTMCE=ALL record
        if (Array.isArray(benchmarkRaw) && benchmarkRaw.length > 0) {
          const drgmbmk = benchmarkRaw.filter((item) => item.type === 'drgmbmk');
          const allRecord = drgmbmk.find((r) => r.DELTMCE === 'ALL');
          if (allRecord) {
            setAfiBenchmarkValue(
              allRecord.DEGEMMB != null
                ? parseFloat(String(allRecord.DEGEMMB).replace(',', '.'))
                : null
            );
            setVleesdikteBenchmarkValue(
              allRecord.DEGEMVD != null
                ? parseFloat(String(allRecord.DEGEMVD).replace(',', '.'))
                : null
            );
            setGewichtBenchmarkValue(
              allRecord.DEGEMGW != null
                ? parseFloat(String(allRecord.DEGEMGW).replace(',', '.'))
                : null
            );
            setSpekdikteBenchmarkValue(
              allRecord.DEGEMSD != null
                ? parseFloat(String(allRecord.DEGEMSD).replace(',', '.'))
                : null
            );
            // DEGEMKT is not among the fields the app has used before; if the
            // webservice does not send it this stays null and the gauge simply
            // renders no black needle.
            setKwaliteitstoeslagBenchmarkValue(
              allRecord.DEGEMKT != null
                ? parseFloat(String(allRecord.DEGEMKT).replace(',', '.'))
                : null
            );
            setGewichtstoeslagBenchmarkValue(
              allRecord.DEGEMGT != null
                ? parseFloat(String(allRecord.DEGEMGT).replace(',', '.'))
                : null
            );
          } else {
            setAfiBenchmarkValue(null);
            setVleesdikteBenchmarkValue(null);
            setGewichtBenchmarkValue(null);
            setSpekdikteBenchmarkValue(null);
            setKwaliteitstoeslagBenchmarkValue(null);
            setGewichtstoeslagBenchmarkValue(null);
          }
        } else {
          setAfiBenchmarkValue(null);
          setVleesdikteBenchmarkValue(null);
          setGewichtBenchmarkValue(null);
          setSpekdikteBenchmarkValue(null);
          setKwaliteitstoeslagBenchmarkValue(null);
          setGewichtstoeslagBenchmarkValue(null);
        }
      } catch (err) {
        if (err.message === 'FORBIDDEN') {
          setAlert(t('alerts.errors.standardErrorMessage'), 'danger')(alertsDispatch);
        } else {
          setAlert(t('alerts.errors.statistics.couldNotFetch'), 'danger')(alertsDispatch);
        }
        setStatisticsData(null);
        setAfiBenchmarkValue(null);
        setVleesdikteBenchmarkValue(null);
        setGewichtBenchmarkValue(null);
        setSpekdikteBenchmarkValue(null);
        setKwaliteitstoeslagBenchmarkValue(null);
        setGewichtstoeslagBenchmarkValue(null);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedFacvstnr]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSelectionChange = (e) => {
    setSelectedFacvstnr(e.target.value);
  };

  return (
    <Fragment>
      <Row className='mt-4'>
        <Col lg='12'>
          <div className='statistics-info' role='note'>
            <span className='statistics-info-icon' aria-hidden='true'>?</span>
            <div>
              <h5 className='statistics-info-title'>{t('statistics.infoTitle')}</h5>
              <p className='statistics-info-text'>{t('statistics.infoText')}</p>
            </div>
          </div>
        </Col>
      </Row>

      {documentsLoading && (
        <Row className='mt-4'>
          <Col lg='12'>
            <PageSpinner />
          </Col>
        </Row>
      )}

      {!documentsLoading && (
      <Row className='mt-4'>
        <Col lg='12'>
          <Card>
            <CardBody>
              <FormGroup>
                <Label for='afrekening-select'>
                  {t('statistics.selectDocument')}
                </Label>
                <Input
                  type='select'
                  id='afrekening-select'
                  value={selectedFacvstnr}
                  onChange={handleSelectionChange}
                >
                  <option value=''>{t('statistics.selectDocumentPlaceholder')}</option>
                  {afrekeningDocuments.map((doc) => (
                    <option key={doc.id} value={doc.factuurvoorstelnummer}>
                      {doc.factuurvoorstelnummer} - {doc.factuurvoorsteldatum} -{' '}
                      {doc.loading_place}
                    </option>
                  ))}
                </Input>
              </FormGroup>
            </CardBody>
          </Card>
        </Col>
      </Row>
      )}

      {loading && (
        <Row className='mt-4'>
          <Col lg='12'>
            <PageSpinner />
          </Col>
        </Row>
      )}

      {!loading && statisticsData && (
        <Row className='mt-4'>
          <Col lg='12'>
            <Card>
              <CardBody>
                <StatisticsChart
                  data={statisticsData}
                  afiBenchmarkValue={afiBenchmarkValue}
                  vleesdikteBenchmarkValue={vleesdikteBenchmarkValue}
                  gewichtBenchmarkValue={gewichtBenchmarkValue}
                  spekdikteBenchmarkValue={spekdikteBenchmarkValue}
                  kwaliteitstoeslagBenchmarkValue={kwaliteitstoeslagBenchmarkValue}
                  gewichtstoeslagBenchmarkValue={gewichtstoeslagBenchmarkValue}
                />
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}
    </Fragment>
  );
};

export default StatisticsComponent;
